<template>
    <DataTable :value="tableData" class="p-datatable-sm payout-table" :paginator="true" :rows="rowsPerPage"
        :totalRecords="totalRecords" :first="(currentPage - 1) * rowsPerPage" :rowsPerPageOptions="[10, 20, 50]"
        :lazy="true" :loading="isLoading" @page="onPage">

        <Column field="username" header="Tutor Name"></Column>
        <Column field="email" header="Email"></Column>

        <Column field="tutorType" header="Type">
            <template #body="{ data }">
                <span class="capitalize">{{ cleanupLables(data.tutorType) }}</span>
            </template>
        </Column>

        <Column field="paymentCount" header="Payments"></Column>

        <Column field="totalClassFee" header="Total Generated">
            <template #body="{ data }">
                {{ formatCurrency(data.totalClassFee ?? 0) }}
            </template>
        </Column>

        <Column field="totalPlatformCommission" header="Commission">
            <template #body="{ data }">
                {{ formatCurrency(data.totalPlatformCommission ?? 0) }}
            </template>
        </Column>

        <Column field="totalPayhereFee" header="PayHere Fee">
            <template #body="{ data }">
                {{ formatCurrency(data.totalPayhereFee ?? 0) }}
            </template>
        </Column>

        <Column field="totalTutorPayout" header="Total Payout">
            <template #body="{ data }">
                <strong>{{ formatCurrency(data.totalTutorPayout ?? 0) }}</strong>
            </template>
        </Column>

        <Column header="Unconfirmed">
            <template #body="{ data }">
                <div class="flex flex-column gap-1">
                    <Tag
                        :value="`${data.unconfirmedSessionCount ?? 0} sessions`"
                        severity="secondary"
                        rounded
                        style="padding: 2px 7px; font-size: 0.65rem; align-self: flex-start;"
                    />
                    <span v-if="data.unconfirmedSessionCount > 0" style="font-size: 0.82rem; font-weight: 500; color: #6b7280;">
                        {{ formatCurrency(data.unconfirmedPayoutAmount ?? 0) }}
                    </span>
                </div>
            </template>
        </Column>

        <Column header="Bank">
            <template #body="{ data }">
                <Button
                    v-if="data.bankDetails"
                    icon="pi pi-building-columns"
                    class="p-button-rounded p-button-text p-button-sm"
                    style="color: #7792D0;"
                    v-tooltip.top="'View Bank Details'"
                    @click="toggleBankPanel($event, data.bankDetails)"
                />
                <span v-else class="text-gray-400 text-sm">N/A</span>
            </template>
        </Column>

        <Column header=" ">
            <template #body="{ data }">
                <Button label="Session Charges" icon="pi pi-users"
                    class="accent-button-transparent p-button-sm p-button-rounded" 
                    style="font-size: 12px; border-radius: 2rem;"
                    @click="viewHistory(data)" />
            </template>
        </Column>

        <template #paginatorstart>
            <div class="text-sm Caption">
                Showing {{ tableData.length }} of {{ totalRecords }} records
            </div>
        </template>

        <template #empty>
            <div class="flex align-items-center justify-content-center" style="min-height: calc(100vh - 600px)">
                <div class="text-center">
                    <i class="pi pi-wallet" style="font-size: 2rem; color: var(--surface-400)"></i>
                    <p class="mt-2 text-color-secondary">No payout records found</p>
                </div>
            </div>
        </template>
    </DataTable>

    <OverlayPanel ref="bankPanel" class="bank-overlay">
        <div v-if="activeBankDetails" class="bank-details-popup">

            <div class="bank-popup-header">
                <div class="bank-header-left">
                    <i class="pi pi-building-columns bank-icon"></i>
                    <span>Bank Transfer Details</span>
                </div>
                <Tag
                    :value="activeBankDetails.isVerified ? 'Verified' : 'Unverified'"
                    :severity="activeBankDetails.isVerified ? 'success' : 'warn'"
                    style="font-size: 10px"
                />
            </div>

            <div class="bank-popup-body">
                <div class="bank-row" v-if="activeBankDetails.accountName">
                    <div class="bank-row-left">
                        <span class="bank-label">Account Name</span>
                        <span class="bank-value">{{ activeBankDetails.accountName }}</span>
                    </div>
                    <Button 
                        :icon="copiedField === 'accountName' ? 'pi pi-check-circle' : 'pi pi-copy'" 
                        class="copy-btn p-button-text p-button-rounded p-button-sm"
                        :class="{ 'text-green-500': copiedField === 'accountName' }"
                        v-tooltip.top="copiedField === 'accountName' ? 'Copied!' : 'Copy'" 
                        @click="copyValue(activeBankDetails.accountName, 'accountName')" 
                    />
                </div>

                <div class="bank-row" v-if="activeBankDetails.accountNumber">
                    <div class="bank-row-left">
                        <span class="bank-label">Account Number</span>
                        <span class="bank-value mono">{{ activeBankDetails.accountNumber }}</span>
                    </div>
                    <Button 
                        :icon="copiedField === 'accountNumber' ? 'pi pi-check-circle' : 'pi pi-copy'" 
                        class="copy-btn p-button-text p-button-rounded p-button-sm"
                        :class="{ 'text-green-500': copiedField === 'accountNumber' }"
                        v-tooltip.top="copiedField === 'accountNumber' ? 'Copied!' : 'Copy'" 
                        @click="copyValue(activeBankDetails.accountNumber, 'accountNumber')" 
                    />
                </div>

                <div class="bank-row" v-if="activeBankDetails.bankName">
                    <div class="bank-row-left">
                        <span class="bank-label">Bank</span>
                        <span class="bank-value">{{ activeBankDetails.bankName }}</span>
                    </div>
                    <Button 
                        :icon="copiedField === 'bankName' ? 'pi pi-check-circle' : 'pi pi-copy'" 
                        class="copy-btn p-button-text p-button-rounded p-button-sm"
                        :class="{ 'text-green-500': copiedField === 'bankName' }"
                        v-tooltip.top="copiedField === 'bankName' ? 'Copied!' : 'Copy'" 
                        @click="copyValue(activeBankDetails.bankName, 'bankName')" 
                    />
                </div>

                <div class="bank-row" v-if="activeBankDetails.branchName">
                    <div class="bank-row-left">
                        <span class="bank-label">Branch</span>
                        <span class="bank-value">{{ activeBankDetails.branchName }}</span>
                    </div>
                    <Button 
                        :icon="copiedField === 'branchName' ? 'pi pi-check-circle' : 'pi pi-copy'" 
                        class="copy-btn p-button-text p-button-rounded p-button-sm"
                        :class="{ 'text-green-500': copiedField === 'branchName' }"
                        v-tooltip.top="copiedField === 'branchName' ? 'Copied!' : 'Copy'" 
                        @click="copyValue(activeBankDetails.branchName, 'branchName')" 
                    />
                </div>

                <div class="bank-row" v-if="activeBankDetails.swiftCode">
                    <div class="bank-row-left">
                        <span class="bank-label">SWIFT Code</span>
                        <span class="bank-value mono">{{ activeBankDetails.swiftCode }}</span>
                    </div>
                    <Button 
                        :icon="copiedField === 'swiftCode' ? 'pi pi-check-circle' : 'pi pi-copy'" 
                        class="copy-btn p-button-text p-button-rounded p-button-sm"
                        :class="{ 'text-green-500': copiedField === 'swiftCode' }"
                        v-tooltip.top="copiedField === 'swiftCode' ? 'Copied!' : 'Copy'" 
                        @click="copyValue(activeBankDetails.swiftCode, 'swiftCode')" 
                    />
                </div>
            </div>

        </div>
    </OverlayPanel>
</template>

<script setup>
import { ref, defineProps, defineEmits } from "vue";
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import OverlayPanel from 'primevue/overlaypanel';
import Tag from 'primevue/tag';
import { cleanupLables } from "@/utils/cleanups";

defineProps({
    tableData: Array,
    isLoading: Boolean,
    rowsPerPage: Number,
    totalRecords: Number,
    currentPage: Number
});

const emit = defineEmits(["row-click", "page-change"]);
const bankPanel = ref(null);
const activeBankDetails = ref(null);
const copiedField = ref(null);

const toggleBankPanel = (event, bankDetails) => {
    activeBankDetails.value = bankDetails;
    copiedField.value = null;
    bankPanel.value.toggle(event);
};

const copyValue = async (value, fieldName) => {
    try {
        await navigator.clipboard.writeText(value);
        copiedField.value = fieldName; 
        
        // Revert the icon back to 'pi-copy' after 1.5 seconds
        setTimeout(() => {
            if (copiedField.value === fieldName) {
                copiedField.value = null;
            }
        }, 1200);
    } catch (err) {
        console.error('Failed to copy text: ', err);
    }
};

const viewHistory = (data) => {
    emit("row-click", data);
};

const onPage = (event) => {
    const page = Math.floor(event.first / event.rows) + 1;
    const limit = event.rows;
    emit("page-change", { page, limit });
};

const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-LK', { style: 'currency', currency: 'LKR' }).format(value);
};
</script>

<style scoped>
.pending-confirmation-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.5rem;
    height: 1.5rem;
    padding: 0 0.4rem;
    border-radius: 9999px;
    background-color: #f97316;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: default;
}

.bank-details-popup {
    width: 360px;
}

.bank-popup-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 0.85rem;
    margin-bottom: 0.85rem;
    border-bottom: 1px solid var(--surface-200);
}

.bank-header-left {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-weight: 600;
    font-size: 0.9rem;
    color: var(--text-color);
}

.bank-icon {
    font-size: 1rem;
    color: #7792D0;
}

.bank-popup-body {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.bank-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.5rem 0.4rem;
    border-radius: 6px;
    transition: background 0.15s;
}

.bank-row:hover {
    background: var(--surface-50);
}

.bank-row-left {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    flex: 1;
    min-width: 0;
}

.bank-label {
    font-size: 0.72rem;
    color: var(--text-color-secondary);
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.bank-value {
    font-size: 0.875rem;
    color: var(--text-color);
    font-weight: 500;
    word-break: break-all;
}

.bank-value.mono {
    font-family: monospace;
    letter-spacing: 0.6px;
}

.copy-btn {
    flex-shrink: 0;
    color: #7792D0 !important;
    width: 2rem !important;
    height: 2rem !important;
    transition: all 0.2s ease-in-out;
}

.copy-btn:hover {
    color: var(--primary-color) !important;
    background: var(--surface-100) !important;
}

/* Optional: Ensure the checkmark color overrides the default gray when active */
.copy-btn.text-green-500 {
    color: #7792D0 !important; 
}
</style>